import * as Api from '@ViewModels';
import { StyleDeclarationValue, StyleSheet, css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { IVolunteerShift, useVolunteerRolesQuery } from '../../../queries/Volunteer';
import { Button } from '../../components-v2/Button';
import { Pill } from '../../components-v2/Pill';
import { THEME } from '../../components-v2/Theme/theme';
import { DeleteConfirmation } from '../../components/DeleteConfirmation';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { TrashIcon } from '../../components/svgs/icons/TrashIcon';
import { ShiftDataSource } from './shiftDataSource';
import { ManageJobsDrawer } from './ManageJobsDrawer';
import { IActivityDateRange, ShiftFormDrawer } from './ShiftFormDrawer';
import { formatShiftTimeRange, groupShiftsByDay } from './shiftScheduling';

interface IProps {
	dataSource: ShiftDataSource;
	activityTimeZone?: string;
	activityDateRange?: IActivityDateRange;
	listStyles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
}

export const ShiftsTab = ({
	dataSource,
	activityTimeZone,
	activityDateRange,
	listStyles = [],
	impersonationContext,
}: IProps) => {
	const { shifts, isLoading } = dataSource;
	const { data: roles = [] } = useVolunteerRolesQuery({ includeArchived: true, impersonationContext });
	const rolesById = useMemo(() => new Map(roles.map(role => [role.id, role.name])), [roles]);

	const [formShift, setFormShift] = useState<IVolunteerShift | null>(null);
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [isManageOpen, setIsManageOpen] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<IVolunteerShift | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const openCreate = () => {
		setFormShift(null);
		setIsFormOpen(true);
	};
	const openEdit = (shift: IVolunteerShift) => {
		setFormShift(shift);
		setIsFormOpen(true);
	};

	const groups = groupShiftsByDay(shifts, activityTimeZone);

	return (
		<div className={css(styles.root)}>
			<div className={css(styles.toolbar)}>
				<Button type='button' onClick={openCreate}>
					Add shift
				</Button>
				<Button type='button' variant='outline' onClick={() => setIsManageOpen(true)}>
					Manage jobs
				</Button>
			</div>

			{isLoading ? (
				<div className={css(styles.loading)}>
					<LoadingSpinner type='large' />
				</div>
			) : groups.length === 0 ? (
				<div className={css(styles.empty)}>
					<img className={css(styles.emptyIllustration)} src='/static/images/empty-state-volunteer.svg' alt='' />
					<h3 className={css(styles.emptyTitle)}>No shifts yet</h3>
					<p className={css(styles.emptyText)}>Add a shift or generate a recurring set across chosen weekdays.</p>
					<Button type='button' onClick={openCreate}>
						Add shift
					</Button>
				</div>
			) : (
				<div className={css(styles.list, ...listStyles)}>
					{groups.map(group => (
						<div key={group.dayKey} className={css(styles.group)}>
							<h3 className={css(styles.dayLabel)}>{group.dayLabel}</h3>
							{group.shifts.map(shift => (
								<div key={shift.id} className={css(styles.shiftRow)}>
									<div className={css(styles.shiftMain)}>
										<span className={css(styles.time)}>{formatShiftTimeRange(shift, activityTimeZone)}</span>
										{shift.location ? <span className={css(styles.muted)}>{shift.location}</span> : null}
										{(shift.roles?.length ?? 0) === 0 ? (
											<span className={css(styles.muted)}>
												{shift.capacity != null ? `${shift.capacity} spots` : 'Open'}
											</span>
										) : null}
										<div className={css(styles.chips)}>
											{(shift.roles ?? []).map(role => (
												<Pill
													key={role.roleId}
													variant='label'
													color='blue'
													label={`${rolesById.get(role.roleId) ?? 'Unknown job'}${
														role.capacity != null ? ` · ${role.capacity}` : ''
													}`}
												/>
											))}
										</div>
									</div>
									<div className={css(styles.shiftActions)}>
										<Button type='button' variant='link' size='sm' onClick={() => openEdit(shift)}>
											Edit
										</Button>
										<Button
											type='button'
											variant='ghost'
											size='icon'
											aria-label='Delete shift'
											onClick={() => setPendingDelete(shift)}
										>
											<TrashIcon width={16} height={16} />
										</Button>
									</div>
								</div>
							))}
						</div>
					))}
				</div>
			)}

			{isFormOpen ? (
				<ShiftFormDrawer
					key={formShift?.id ?? 'new'}
					isOpen={isFormOpen}
					onClose={() => setIsFormOpen(false)}
					activityTimeZone={activityTimeZone}
					activityDateRange={activityDateRange}
					shift={formShift ?? undefined}
					createShifts={dataSource.createShifts}
					updateShift={dataSource.updateShift}
					impersonationContext={impersonationContext}
				/>
			) : null}

			{isManageOpen ? (
				<ManageJobsDrawer
					isOpen={isManageOpen}
					onClose={() => setIsManageOpen(false)}
					impersonationContext={impersonationContext}
				/>
			) : null}

			<DeleteConfirmation
				isOpen={!!pendingDelete}
				title='Delete shift'
				bodyText='Are you sure you want to delete this shift?'
				destructiveButtonText='Delete'
				showSpinner={isDeleting}
				onFinish={async confirmed => {
					if (!confirmed) {
						setPendingDelete(null);
						return;
					}
					if (pendingDelete) {
						setIsDeleting(true);
						try {
							await dataSource.archiveShift(pendingDelete.id);
							toast.success('Shift deleted');
							setPendingDelete(null);
						} catch {
							toast.error('Failed to delete the shift');
						} finally {
							setIsDeleting(false);
						}
					}
				}}
			/>
		</div>
	);
};

const styles = StyleSheet.create({
	root: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.md, paddingTop: THEME.spacing.sm },
	toolbar: { display: 'flex', gap: THEME.spacing.sm, justifyContent: 'flex-end' },
	loading: { alignItems: 'center', display: 'flex', justifyContent: 'center', padding: THEME.spacing.xxl },
	empty: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.xxl,
		textAlign: 'center',
	},
	emptyIllustration: { height: 'auto', marginBottom: THEME.spacing.sm, maxWidth: 320, width: '100%' },
	emptyTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	emptyText: { color: THEME.colors.text.light, fontSize: THEME.typography.fontSizes.sm, margin: 0, maxWidth: 380 },
	list: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.md },
	group: { display: 'flex', flexDirection: 'column', gap: THEME.spacing.sm },
	dayLabel: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	shiftRow: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		justifyContent: 'space-between',
		padding: THEME.spacing.sm,
	},
	shiftMain: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: THEME.spacing.sm },
	shiftActions: { alignItems: 'center', display: 'flex', gap: THEME.spacing.xs },
	time: { color: THEME.colors.text.main, fontSize: THEME.typography.fontSizes.sm },
	muted: { color: THEME.colors.text.light, fontSize: THEME.typography.fontSizes.sm },
	chips: { display: 'flex', flexWrap: 'wrap', gap: THEME.spacing.xs },
});
